import { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, PushPin, ArrowsLeftRight } from "@phosphor-icons/react";
import { CaptureEditPanel } from "./CaptureEditPanel";

const MIN_SPLIT_PERCENT = 20;
const MAX_SPLIT_PERCENT = 80;

// Desktop-only tabbed split view (max 2 notes side-by-side). Mobile keeps
// the original single-note modal (CaptureEditDialog in CapturePreviewDialog)
// entirely untouched - this component is only ever mounted from Dashboard.jsx.
export function NotesWorkspace({ tabs, focusedTabId, onFocusTab, onCloseTab, onDelete, onSaved, pinnedTabId, onTogglePin, onGenerateLink, onAddTag, onSwapTabs, tags, onToggleFavorite }) {
  const { t } = useTranslation();
  const panelRefs = useRef({});
  const panesContainerRef = useRef(null);
  const draggingRef = useRef(false);
  // Percentage width of the left/first pane when 2 tabs are open - draggable
  // via the divider so users can widen whichever note needs more room.
  const [splitPercent, setSplitPercent] = useState(50);

  const handleDividerMove = useCallback((e) => {
    if (!draggingRef.current || !panesContainerRef.current) return;
    const rect = panesContainerRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setSplitPercent(Math.min(MAX_SPLIT_PERCENT, Math.max(MIN_SPLIT_PERCENT, pct)));
  }, []);

  const stopDividerDrag = useCallback(() => {
    draggingRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", handleDividerMove);
    document.removeEventListener("mouseup", stopDividerDrag);
  }, [handleDividerMove]);

  const startDividerDrag = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleDividerMove);
    document.addEventListener("mouseup", stopDividerDrag);
  };

  useEffect(() => stopDividerDrag, [stopDividerDrag]);

  useEffect(() => {
    if (tabs.length !== 2) setSplitPercent(50);
  }, [tabs.length]);

  if (!tabs || tabs.length === 0) return null;

  const handleCloseTab = async (id) => {
    const panel = panelRefs.current[id];
    if (panel) await panel.finalizeAndSave();
    delete panelRefs.current[id];
    onCloseTab(id);
  };

  const tabLabel = (tab) => tab.title?.trim() || (tab.type === "text" ? t("editDialog.titleText") : t("editDialog.titleImage"));

  return (
    <div className="mb-8 border border-border" data-testid="notes-split-view">
      <div className="flex border-b border-border bg-secondary/40 overflow-x-auto" data-testid="notes-tab-bar">
        {tabs.map((tab) => {
          const isPinned = tab.id === pinnedTabId;
          return (
            <div
              key={tab.id}
              data-testid={`note-tab-${tab.id}`}
              onClick={() => onFocusTab(tab.id)}
              className={`group flex items-center gap-1.5 px-3 py-2.5 border-r border-border cursor-pointer max-w-[240px] shrink-0 font-mono-tag text-xs transition-colors ${
                tab.id === focusedTabId ? "bg-background text-primary" : "text-muted-foreground hover:bg-background/60"
              }`}
            >
              <button
                type="button"
                data-testid={`note-tab-pin-${tab.id}`}
                title={isPinned ? t("editDialog.unpinNote") : t("editDialog.pinNote")}
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePin?.(tab.id);
                }}
                className={`shrink-0 transition-opacity ${
                  isPinned ? "text-primary opacity-100" : "text-muted-foreground opacity-0 group-hover:opacity-100"
                }`}
              >
                <PushPin size={12} weight={isPinned ? "fill" : "regular"} />
              </button>
              <span className="truncate">{tabLabel(tab)}</span>
              {!isPinned && (
                <button
                  type="button"
                  data-testid={`note-tab-close-${tab.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseTab(tab.id);
                  }}
                  className="text-muted-foreground hover:text-destructive shrink-0"
                >
                  <X size={12} weight="bold" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div ref={panesContainerRef} className="flex" data-testid="notes-split-panes">
        {tabs.length === 2 ? (
          <>
            <div
              key={tabs[0].id}
              className="min-w-0 p-5 max-h-[75vh] overflow-y-auto [scrollbar-gutter:stable]"
              style={{ flex: `0 0 ${splitPercent}%` }}
              data-testid={`note-pane-${tabs[0].id}`}
            >
              <CaptureEditPanel
                ref={(el) => { panelRefs.current[tabs[0].id] = el; }}
                capture={tabs[0]}
                onDelete={onDelete}
                onSaved={onSaved}
                onGenerateLink={onGenerateLink}
                onAddTag={onAddTag}
                tags={tags}
                onToggleFavorite={onToggleFavorite}
              />
            </div>
            <div
              data-testid="notes-split-divider"
              onMouseDown={startDividerDrag}
              className="relative w-1.5 shrink-0 cursor-col-resize bg-border hover:bg-primary/50 active:bg-primary transition-colors"
            >
              <button
                type="button"
                data-testid="notes-swap-panes-button"
                title={t("editDialog.swapPanes")}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onSwapTabs?.();
                }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center bg-card border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors z-10"
              >
                <ArrowsLeftRight size={13} weight="bold" />
              </button>
            </div>
            <div
              key={tabs[1].id}
              className="min-w-0 flex-1 p-5 max-h-[75vh] overflow-y-auto [scrollbar-gutter:stable] border-l border-border"
              data-testid={`note-pane-${tabs[1].id}`}
            >
              <CaptureEditPanel
                ref={(el) => { panelRefs.current[tabs[1].id] = el; }}
                capture={tabs[1]}
                onDelete={onDelete}
                onSaved={onSaved}
                onGenerateLink={onGenerateLink}
                onAddTag={onAddTag}
                tags={tags}
                onToggleFavorite={onToggleFavorite}
              />
            </div>
          </>
        ) : (
          <div
            key={tabs[0].id}
            className="min-w-0 flex-1 p-5 max-h-[75vh] overflow-y-auto [scrollbar-gutter:stable]"
            data-testid={`note-pane-${tabs[0].id}`}
          >
            <CaptureEditPanel
              ref={(el) => { panelRefs.current[tabs[0].id] = el; }}
              capture={tabs[0]}
              onDelete={onDelete}
              onSaved={onSaved}
              onGenerateLink={onGenerateLink}
              onAddTag={onAddTag}
              tags={tags}
              onToggleFavorite={onToggleFavorite}
            />
          </div>
        )}
      </div>
    </div>
  );
}
